import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import SafeIcon from '../common/SafeIcon';
import OrderSmsPreferences from '../components/common/OrderSmsPreferences';
import { detectCity } from '../services/locationService';
import { 
  getNotificationPermission, 
  requestNotificationPermission, 
  NOTIFICATION_STORAGE_KEY,
  checkNotificationSupport 
} from '../services/notificationService';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiMail, FiPhone, FiMapPin, FiSave, FiCheckCircle, FiLoader, FiArrowRight, FiTarget, FiAlertCircle, FiBriefcase, FiBell, FiBellOff } = FiIcons;

const ProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, updateProfile, becomeSeller, loading: authLoading } = useSupabaseAuth();
  
  const [formData, setFormData] = useState({ name: '', phone: '', location: '', is_seller: false });
  const [loading, setLoading] = useState(false);
  const [locLoading, setLocLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  // Notification State
  const [notifPermission, setNotifPermission] = useState(getNotificationPermission());
  const [notifLoading, setNotifLoading] = useState(false);

  const isInitialMount = useRef(true);
  const becomeSellerRef = useRef(null);

  useEffect(() => {
    if (profile && (isInitialMount.current || profile.id !== formData.id)) {
      setFormData({
        id: profile.id,
        name: profile.name || '',
        phone: profile.phone || '',
        location: profile.location || '',
        is_seller: profile.is_seller || false
      });
      isInitialMount.current = false;
    }
  }, [profile?.id]);

  useEffect(() => {
    if (location.hash === '#become-seller' && becomeSellerRef.current) {
      becomeSellerRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location.hash]);

  const handleToggleNotifications = async () => {
    setNotifLoading(true);
    const granted = await requestNotificationPermission();
    setNotifPermission(getNotificationPermission());
    setNotifLoading(false);
    
    if (!granted && getNotificationPermission() === 'denied') {
      alert('Notifications are blocked by your browser settings. Please enable them in your browser site settings to receive alerts.');
    }
  };

  const handleDetectLocation = async () => {
    setLocLoading(true);
    try {
      const locationData = await detectCity();
      setFormData(prev => ({ ...prev, location: locationData.fullName }));
    } catch (err) {
      console.error('Location detection failed:', err);
    } finally {
      setLocLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // 1. Handle Seller Status Upgrade separately if requested
      const becomingSeller = formData.is_seller && !profile.is_seller;
      
      // 2. Update standard profile info
      const updates = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        location: formData.location.trim()
      };
      
      await updateProfile(updates);

      // 3. If becoming a seller, call the secure RPC
      if (becomingSeller) {
        await becomeSeller();
        setSuccess(true);
        setTimeout(() => navigate('/seller-dashboard'), 1500);
      } else {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Profile update failed:', err);
      setError(err.message || 'Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading && !profile) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gray-50">
        <SafeIcon icon={FiLoader} className="w-8 h-8 text-emerald-600 animate-spin" />
      </div>
    );
  }

  if (!user && !authLoading) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gray-50 py-12 px-4">
      <div className="max-w-3xl mx-auto space-y-8">
        <div className="flex justify-between items-end">
          <div>
            <h1 className="text-4xl font-black text-gray-900 tracking-tight">Account Settings</h1>
            <p className="text-gray-500 font-medium">Manage your personal information</p>
          </div>
          {profile?.is_seller && (
            <button onClick={() => navigate('/seller-dashboard')} className="flex items-center px-4 py-2 bg-emerald-50 text-emerald-700 rounded-xl font-bold text-sm hover:bg-emerald-100 transition-all">
              Dashboard <SafeIcon icon={FiArrowRight} className="ml-2" />
            </button>
          )}
        </div>

        {/* Notifications Card */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-[2.5rem] shadow-xl p-8 md:p-10 border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center mr-4 text-indigo-600">
                <SafeIcon icon={notifPermission === 'granted' ? FiBell : FiBellOff} />
              </div>
              <div>
                <h2 className="text-xl font-black text-gray-900">Browser Notifications</h2>
                <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">Get alerts for messages & orders</p>
              </div>
            </div>
            <button
              onClick={handleToggleNotifications}
              disabled={notifLoading || !checkNotificationSupport()}
              className={`px-6 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all ${
                notifPermission === 'granted' 
                ? 'bg-emerald-50 text-emerald-600' 
                : 'bg-indigo-600 text-white shadow-lg shadow-indigo-100'
              } disabled:opacity-50`}
            >
              {notifLoading ? <FiLoader className="animate-spin" /> : 
               notifPermission === 'granted' ? 'Enabled' : 
               notifPermission === 'denied' ? 'Blocked' : 'Enable Alerts'}
            </button>
          </div>
          {!checkNotificationSupport() && (
            <p className="mt-4 text-[10px] text-red-500 font-bold uppercase">Notifications are not supported by this browser.</p>
          )}
        </motion.div>

        <OrderSmsPreferences userId={user?.id} />

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-[2.5rem] shadow-xl p-8 md:p-10 border border-gray-100">
          <h2 className="text-xl font-black text-gray-900 mb-8 flex items-center text-emerald-600">
            <SafeIcon icon={FiUser} className="mr-3" /> Personal Profile
          </h2>
          {success && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="mb-8 bg-emerald-50 border border-emerald-100 text-emerald-700 px-5 py-4 rounded-2xl flex items-center font-bold text-sm">
              <SafeIcon icon={FiCheckCircle} className="mr-3 text-lg" /> Your changes have been saved!
            </motion.div>
          )}
          {error && (
            <div className="mb-8 bg-red-50 border border-red-100 text-red-700 px-5 py-4 rounded-2xl flex items-center font-bold text-sm">
              <SafeIcon icon={FiAlertCircle} className="mr-3 text-lg" /> {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Full Name</label>
                <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-5 py-4 bg-gray-50 border border-transparent rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:bg-white outline-none transition-all font-bold" placeholder="Your Name" />
              </div>
              <div>
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Email (Read-only)</label>
                <div className="relative">
                  <SafeIcon icon={FiMail} className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-300" />
                  <input type="text" disabled value={user?.email || ''} className="pl-12 block w-full px-5 py-4 bg-gray-100 border border-transparent rounded-2xl text-gray-400 font-medium cursor-not-allowed" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Phone Number</label>
                <div className="relative">
                  <SafeIcon icon={FiPhone} className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="pl-12 block w-full px-5 py-4 bg-gray-50 border border-transparent rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:bg-white outline-none transition-all font-bold" placeholder="+1 (555) 000-0000" />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Location</label>
                <div className="relative">
                  <SafeIcon icon={FiMapPin} className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="text" value={formData.location} placeholder="City,Province" onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="pl-12 pr-14 block w-full px-5 py-4 bg-gray-50 border border-transparent rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:bg-white outline-none transition-all font-bold" />
                  <button type="button" onClick={handleDetectLocation} disabled={locLoading} className="absolute right-3 top-1/2 -translate-y-1/2 p-2.5 text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all disabled:opacity-50">
                    <SafeIcon icon={locLoading ? FiLoader : FiTarget} className={locLoading ? 'animate-spin' : ''} />
                  </button>
                </div>
              </div>
            </div>
            {!profile?.is_seller && (
              <div ref={becomeSellerRef} className="mt-12 p-8 bg-indigo-50 rounded-[2rem] border border-indigo-100 space-y-4">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm">
                    <SafeIcon icon={FiBriefcase} className="text-indigo-600 text-xl" />
                  </div>
                  <div>
                    <h3 className="font-black text-indigo-900 uppercase tracking-widest text-sm">Become a Seller</h3>
                    <p className="text-xs text-indigo-500 font-bold">Share your creations with the neighborhood</p>
                  </div>
                </div>
                <div className="flex items-center p-4 bg-white rounded-2xl border border-indigo-100">
                  <input id="is_seller" type="checkbox" checked={formData.is_seller} onChange={(e) => setFormData({ ...formData, is_seller: e.target.checked })} className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded-lg cursor-pointer" />
                  <label htmlFor="is_seller" className="ml-3 block text-sm font-black text-slate-700 cursor-pointer"> Enable seller features for my account </label>
                </div>
              </div>
            )}
            <div className="flex justify-end pt-6">
              <button type="submit" disabled={loading} className="w-full md:w-auto px-10 py-4 bg-emerald-600 text-white rounded-2xl font-black hover:bg-emerald-700 transition-all flex items-center justify-center disabled:opacity-50 shadow-xl shadow-emerald-100">
                {loading ? <><SafeIcon icon={FiLoader} className="mr-3 animate-spin" /> Saving...</> : <><SafeIcon icon={FiSave} className="mr-3" /> Save Changes</>}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default ProfilePage;
